import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { bevelShadow, primaryBtn } from "../theme/styles";

export interface NavLocation {
  id: string;
  name: string;
  description: string;
  buttonLabel: string;
  to: string;
  /** Authored hint shown beneath the tile name when locked. */
  lockedHint: string | null;
  /** For NPC-kind children: the referenced NPC and its default
   *  mood, used to fall back to `<npc_ref>_<DEFAULT_MOOD>.png` when
   *  no `<id>.png` location-style portrait exists. */
  npcRef?: string | null;
  defaultMood?: string | null;
}

const PORTRAIT_SIZE = 120;

export function LocationCardList({ locations }: { locations: NavLocation[] }) {
  const theme = pixelTheme;
  const navigate = useNavigate();

  return (
    <ul
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gap: 12,
      }}
    >
      {locations.map((location) => {
        const locked = location.lockedHint !== null;
        return (
          <li
            key={location.id}
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              boxShadow: bevelShadow,
              padding: 14,
              display: "grid",
              gridTemplateColumns: `${PORTRAIT_SIZE}px 1fr`,
              gap: 14,
              alignItems: "start",
              opacity: locked ? 0.55 : 1,
            }}
          >
            <Portrait
              id={location.id}
              npcRef={location.npcRef}
              defaultMood={location.defaultMood}
            />
            <div style={{ display: "grid", gap: 10 }}>
              <h2
                style={{
                  margin: 0,
                  fontFamily: theme.fontDisplay,
                  fontSize: 13,
                  letterSpacing: theme.letterSpacing,
                  color: locked ? theme.inkFaint : theme.accent,
                }}
              >
                {location.name}
              </h2>
              <p style={{ margin: 0, color: theme.inkDim, lineHeight: 1.45 }}>
                {location.description}
              </p>
              {locked && location.lockedHint && (
                <p
                  style={{
                    margin: 0,
                    fontFamily: theme.fontDisplay,
                    fontSize: 10,
                    letterSpacing: theme.letterSpacing,
                    color: E32.scarlet,
                  }}
                >
                  LOCKED · {location.lockedHint}
                </p>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => {
                    if (!locked) navigate(location.to);
                  }}
                  disabled={locked}
                  {...primaryBtn(theme, locked)}
                >
                  {locked ? "LOCKED" : location.buttonLabel}
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// A square portrait pulled by convention from
// `/portraits/locations/<id>.png`. For NPC tiles where no
// location-style portrait exists at that path, fall back to
// `/portraits/npcs/<npc_ref>_<DEFAULT_MOOD>.png` (the NPC's
// default-mood portrait — e.g. `ensign_ned_OFFICIOUS.png`). If both
// 404, render the frame as a plain tinted square so the layout
// doesn't shift.
function Portrait({
  id,
  npcRef,
  defaultMood,
}: {
  id: string;
  npcRef?: string | null;
  defaultMood?: string | null;
}) {
  const theme = pixelTheme;

  // Try in order: location-style portrait → moodied NPC portrait
  // (only the NPC-kind children carry a default mood) → bare
  // <id>.png in npcs/ (covers shops with an NPC portrait but no
  // dedicated location art, e.g. the garrison quartermaster).
  const candidates: string[] = [
    `/portraits/locations/${id}.png`,
    `/portraits/npcs/${id}.png`,
  ];
  if (npcRef && defaultMood) {
    candidates.splice(1, 0, `/portraits/npcs/${npcRef}_${defaultMood.toUpperCase()}.png`);
  }

  const [attempt, setAttempt] = useState(0);
  // Reset to the first candidate when the inputs change.
  useEffect(() => {
    setAttempt(0);
  }, [id, npcRef, defaultMood]);

  const src = candidates[attempt] ?? null;

  return (
    <div
      style={{
        width: PORTRAIT_SIZE,
        height: PORTRAIT_SIZE,
        background: theme.panelEdge,
        border: `2px solid ${theme.ink}`,
        boxShadow: `2px 2px 0 0 ${theme.ink}`,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {src && (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={() => setAttempt((a) => a + 1)}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            imageRendering: "pixelated",
          }}
        />
      )}
    </div>
  );
}
